// ─────────────────────────────────────────────────────────────────
// Glass-i — RFQ detail (Stage 6RFQ). One screen drives the whole
// lifecycle. The hero is a bid-comparison matrix — vendors as columns,
// requested items as rows — because the page's single job is deciding
// who to award. A recommendation banner names the winner and the
// saving; everything else stays quiet. AntD + app blue/slate theme.
//
// The comparison matrix is a transposed, mixed-row data-viz that AntD's
// row-oriented Table can't express, so it's a scoped custom table under
// `.rfq-v2`. All other surfaces use AntD primitives (Steps, Timeline,
// Tag, Descriptions, Drawer, Modal, Segmented).
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Button, Card, Descriptions, Divider, Drawer, Form, InputNumber,
  Input, Modal, Segmented, Select, Space, Spin, Steps, Table, Tag, Timeline,
  Tooltip, message
} from 'antd';
import {
  ArrowLeftOutlined, SendOutlined, PlusOutlined, PrinterOutlined,
  TrophyOutlined, StopOutlined, ReloadOutlined, DollarOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { rfqApi } from './rfqApi';
import { supplierApi } from '../masters/suppliers/supplierApi';
import './RfqDetailPage.css';
import {
  rfqStatusLabel, rfqStatusColor, vendorStatusLabel, vendorStatusColor,
  canEditRfq, canSendRfq, canQuoteRfq, canAwardRfq, canCancelRfq, money,
  rfqStep, RFQ_STEP_TITLES, responsePct, lowestQuote
} from './rfqHelpers';

const _inr0 = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });
const money0 = (n) => _inr0.format(Number(n) || 0);

// ── Live quote editor — recomputes each line's amount and a running
//    total as the buyer types, so the figure they save is the figure
//    they see. Mirrors the backend math (base_rate × qty + charges).
function QuoteEditor({ form, lines }) {
  const linesVal = Form.useWatch('lines', form) || [];
  const freight = Number(Form.useWatch('freight_charges', form)) || 0;
  const incidental = Number(Form.useWatch('incidental_charges', form)) || 0;
  const amt = (i) => (Number(linesVal[i]?.base_rate) || 0) * (Number(lines[i]?.qty) || 0);
  const subtotal = lines.reduce((s, _l, i) => s + amt(i), 0);
  const grand = subtotal + freight + incidental;

  return (
    <>
      <Table size="small" rowKey={(_, i) => i} pagination={false} className="rfq-quote-grid"
        dataSource={lines}
        columns={[
          { title: '#', dataIndex: 'lineNo', width: 40 },
          { title: 'Item', dataIndex: 'itemName',
            render: (v, r) => (<div><div style={{ fontWeight: 600 }}>{v}</div>
              {r.itemCode ? <div className="rfq-muted">{r.itemCode}</div> : null}</div>) },
          { title: 'Qty', dataIndex: 'qty', width: 64, align: 'right' },
          { title: 'Base rate', width: 116, render: (_v, _r, i) => (
            <Form.Item name={['lines', i, 'base_rate']} noStyle>
              <InputNumber min={0} step={0.01} controls={false} style={{ width: '100%' }} /></Form.Item>) },
          { title: 'HSN', width: 92, render: (_v, _r, i) => (
            <Form.Item name={['lines', i, 'hsn_code']} noStyle><Input /></Form.Item>) },
          { title: 'GST %', width: 70, render: (_v, _r, i) => (
            <Form.Item name={['lines', i, 'gst_rate']} noStyle>
              <InputNumber min={0} max={28} controls={false} style={{ width: '100%' }} /></Form.Item>) },
          { title: 'Lead time', width: 110, render: (_v, _r, i) => (
            <Form.Item name={['lines', i, 'lead_time_text']} noStyle><Input placeholder="2 wks" /></Form.Item>) },
          { title: 'Amount', width: 110, align: 'right',
            render: (_v, _r, i) => <span className="rfq-amt">₹ {money(amt(i))}</span> }
        ]} />
      {/* hidden rfq_line_id per row */}
      {lines.map((l, i) => (
        <Form.Item key={l.id} name={['lines', i, 'rfq_line_id']} hidden initialValue={l.id}><Input /></Form.Item>
      ))}
      <Divider style={{ margin: '14px 0' }} />
      <div className="rfq-quote-foot">
        <Space size="large" wrap>
          <Form.Item label="Freight & logistics (₹)" name="freight_charges" style={{ marginBottom: 0 }}>
            <InputNumber min={0} step={0.01} controls={false} style={{ width: 180 }} /></Form.Item>
          <Form.Item label="Incidental & forwarding (₹)" name="incidental_charges" style={{ marginBottom: 0 }}>
            <InputNumber min={0} step={0.01} controls={false} style={{ width: 180 }} /></Form.Item>
        </Space>
        <div className="rfq-quote-totals">
          <div><span>Goods subtotal</span><b>₹ {money(subtotal)}</b></div>
          <div><span>Freight + incidental</span><b>₹ {money(freight + incidental)}</b></div>
          <div className="rfq-quote-totals__grand"><span>Quote total</span><b>₹ {money(grand)}</b></div>
        </div>
      </div>
    </>
  );
}

export default function RfqDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [rfq, setRfq] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [unitMode, setUnitMode] = useState('rate'); // 'rate' | 'amt'

  // vendor add
  const [addOpen, setAddOpen] = useState(false);
  const [supplierOpts, setSupplierOpts] = useState([]);
  const [pickedSuppliers, setPickedSuppliers] = useState([]);

  // quote drawer
  const [quoteVendor, setQuoteVendor] = useState(null);
  const [quoteForm] = Form.useForm();

  // viewer
  const [viewHtml, setViewHtml] = useState(null);
  const iframeRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRfq(await rfqApi.get(id)); }
    catch (e) { message.error(e?.response?.data?.error?.message || e.message || 'Failed to load RFQ.'); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const run = useCallback(async (key, fn, okMsg) => {
    setBusy(key);
    try { const r = await fn(); if (okMsg) message.success(okMsg); return r; }
    catch (e) { message.error(e?.response?.data?.error?.message || e.message || 'Action failed.'); throw e; }
    finally { setBusy(null); }
  }, []);

  // ── vendors add ──────────────────────────────────────────────
  const searchSuppliers = useCallback(async (q) => {
    try {
      const res = await supplierApi.list({
        search: q || '', page: 1, pageSize: 20,
        filters: [{ field: 'status', value: 'active', op: 'equals' }]
      });
      const rows = Array.isArray(res) ? res : (res.rows || []);
      setSupplierOpts(rows.map((s) => ({
        value: s.id,
        label: `${s.supplierCode || ''} — ${s.supplierName || ''}${s.email ? ` · ${s.email}` : ''}`
      })));
    } catch (_e) { setSupplierOpts([]); }
  }, []);

  const submitAddVendors = async () => {
    if (!pickedSuppliers.length) { setAddOpen(false); return; }
    await run('add', () => rfqApi.addVendors(id, pickedSuppliers), 'Vendors added.');
    setAddOpen(false); setPickedSuppliers([]); load();
  };

  // ── send ─────────────────────────────────────────────────────
  const doSend = () => {
    Modal.confirm({
      title: 'Send this RFQ to vendors?',
      content: 'Each vendor with an email on file will receive the RFQ document. Vendors without an email are skipped.',
      okText: 'Send', onOk: async () => { await run('send', () => rfqApi.send(id, []), 'RFQ sent.'); load(); }
    });
  };

  // ── quote drawer ─────────────────────────────────────────────
  const openQuote = (vendor) => {
    setQuoteVendor(vendor);
    const byLine = {};
    (vendor.quoteLines || []).forEach((q) => { byLine[q.rfqLineId] = q; });
    quoteForm.setFieldsValue({
      freight_charges: vendor.freightCharges || 0,
      incidental_charges: vendor.incidentalCharges || 0,
      lines: (rfq.lines || []).map((l) => ({
        rfq_line_id: l.id,
        base_rate: byLine[l.id]?.baseRate || 0,
        hsn_code: byLine[l.id]?.hsnCode || '',
        gst_rate: byLine[l.id]?.gstRate || 18,
        lead_time_text: byLine[l.id]?.leadTimeText || ''
      }))
    });
  };
  const submitQuote = async () => {
    const v = await quoteForm.validateFields();
    await run('quote', () => rfqApi.recordQuote(id, quoteVendor.id, v), 'Quote recorded.');
    setQuoteVendor(null); load();
  };

  // ── award ────────────────────────────────────────────────────
  const doAward = (vendor) => {
    Modal.confirm({
      title: `Award ${vendor.supplierName} and create a PO?`,
      content: 'This converts the winning quote into a DRAFT Purchase Order (rates pre-filled) and marks the source request converted.',
      okText: 'Award & create PO',
      onOk: async () => {
        const r = await run('award', () => rfqApi.award(id, vendor.id), 'Awarded — PO created.');
        if (r && r.purchaseOrderId) navigate(`/erp/purchase/orders/${r.purchaseOrderId}`);
        else load();
      }
    });
  };

  // ── cancel ───────────────────────────────────────────────────
  const doCancel = () => {
    let reason = '';
    Modal.confirm({
      title: 'Cancel this RFQ?',
      content: <Input.TextArea rows={3} placeholder="Optional reason" onChange={(e) => { reason = e.target.value; }} />,
      okText: 'Cancel RFQ', okButtonProps: { danger: true },
      onOk: async () => { await run('cancel', () => rfqApi.cancel(id, reason), 'RFQ cancelled.'); load(); }
    });
  };

  // ── view / print ─────────────────────────────────────────────
  const viewDoc = async (vendor) => {
    try {
      const html = await rfqApi.reportHtml(id, vendor ? vendor.id : null);
      setViewHtml(html);
    } catch (e) { message.error(e?.response?.data?.error?.message || e.message || 'Failed to render document.'); }
  };
  const printDoc = () => {
    const w = iframeRef.current && iframeRef.current.contentWindow;
    if (w) { w.focus(); w.print(); }
  };

  // ── derived comparison model ─────────────────────────────────
  const model = useMemo(() => {
    if (!rfq) return null;
    const lines = rfq.lines || [];
    const vendors = rfq.vendors || [];
    const responded = vendors.filter((v) => v.status === 'RESPONDED');

    // vendorId -> { lineId -> quoteLine }
    const byVL = {};
    vendors.forEach((v) => {
      const m = {};
      (v.quoteLines || []).forEach((q) => { m[q.rfqLineId] = q; });
      byVL[v.id] = m;
    });

    // lowest base_rate per line among responded vendors
    const lowestPerLine = {};
    lines.forEach((l) => {
      let best = null;
      responded.forEach((v) => {
        const q = byVL[v.id][l.id];
        if (q && Number(q.baseRate) > 0 && (best == null || Number(q.baseRate) < best.rate)) {
          best = { vendorId: v.id, rate: Number(q.baseRate) };
        }
      });
      if (best) lowestPerLine[l.id] = best.vendorId;
    });

    const lowest = lowestQuote(vendors); // { id, total, savings, hasRivals } | null
    const totals = responded.map((v) => Number(v.quoteTotal) || 0);
    const minTotal = totals.length ? Math.min(...totals) : 0;
    const maxTotal = totals.length ? Math.max(...totals) : 0;
    const maxDelta = Math.max(0, maxTotal - minTotal);

    // split-buy: sum of the cheapest line across vendors (goods only)
    let splitGoods = 0; let splitDiffers = false;
    lines.forEach((l) => {
      const winner = lowestPerLine[l.id];
      if (!winner) return;
      const q = byVL[winner][l.id];
      splitGoods += Number(q.baseRate) * Number(l.qty || 0);
      if (lowest && winner !== lowest.id) splitDiffers = true;
    });

    return { lines, vendors, responded, byVL, lowestPerLine, lowest, minTotal, maxTotal, maxDelta, splitGoods, splitDiffers };
  }, [rfq]);

  if (!rfq && loading) return <div style={{ padding: 24 }}><Spin /></div>;
  if (!rfq) return <div style={{ padding: 24 }}>RFQ not found.</div>;

  const { lines, vendors, responded, byVL, lowestPerLine, lowest, maxDelta, splitGoods, splitDiffers } = model;
  const respondedCount = responded.length;
  const pct = responsePct(respondedCount, vendors.length);
  const step = rfqStep(rfq);
  const editable = canEditRfq(rfq);
  const showCompare = canAwardRfq(rfq) && lowest;
  const winnerVendor = lowest ? vendors.find((v) => v.id === lowest.id) : null;
  const dueIn = rfq.submissionDueDate
    ? dayjs(rfq.submissionDueDate).startOf('day').diff(dayjs().startOf('day'), 'day') : null;

  const goodsSubtotal = (v) => (lines.reduce((s, l) => {
    const q = byVL[v.id][l.id];
    return s + (q ? (Number(q.lineAmount) || Number(q.baseRate) * Number(l.qty || 0)) : 0);
  }, 0));
  const leadFor = (v) => {
    const set = new Set();
    (v.quoteLines || []).forEach((q) => { if (q.leadTimeText) set.add(q.leadTimeText); });
    return set.size ? Array.from(set).join(', ') : '—';
  };

  // Lifecycle rail with dated captions.
  const stepItems = RFQ_STEP_TITLES.map((t, i) => {
    let desc = null;
    if (i === 0) desc = rfq.createdAt ? `Raised ${dayjs(rfq.createdAt).format('DD MMM')}` : null;
    if (i === 1) desc = rfq.sentAt ? `${vendors.length} vendors · ${dayjs(rfq.sentAt).format('DD MMM')}` : `${vendors.length} vendors`;
    if (i === 2) desc = `${respondedCount} of ${vendors.length} in`;
    if (i === 3) desc = rfq.convertedPoId ? 'PO created' : 'pending';
    return { title: t, description: desc };
  });

  // Activity timeline from real timestamps.
  const tl = [];
  responded
    .filter((v) => v.respondedAt)
    .sort((a, b) => dayjs(b.respondedAt).valueOf() - dayjs(a.respondedAt).valueOf())
    .forEach((v) => tl.push({
      color: 'green',
      children: <div><div className="rfq-tl-h">{v.supplierName} quoted</div>
        <div className="rfq-tl-m">₹ {money(v.quoteTotal)} · {dayjs(v.respondedAt).format('DD MMM, h:mm a')}</div></div>
    }));
  if (rfq.sentAt) tl.push({
    color: 'blue',
    children: <div><div className="rfq-tl-h">Sent to {vendors.length} vendor{vendors.length === 1 ? '' : 's'}</div>
      <div className="rfq-tl-m">{dayjs(rfq.sentAt).format('DD MMM, h:mm a')}</div></div>
  });
  tl.push({
    color: 'gray',
    children: <div><div className="rfq-tl-h">RFQ raised{rfq.prNoSnapshot ? ` from ${rfq.prNoSnapshot}` : ''}</div>
      <div className="rfq-tl-m">{rfq.contactPerson ? `by ${rfq.contactPerson} · ` : ''}{rfq.createdAt ? dayjs(rfq.createdAt).format('DD MMM, h:mm a') : ''}</div></div>
  });

  const rateCell = (v, l) => {
    const q = byVL[v.id][l.id];
    const isWin = lowest && v.id === lowest.id;
    if (v.status !== 'RESPONDED' || !q || !(Number(q.baseRate) > 0)) {
      return <td key={v.id} className={`rate await-cell${isWin ? ' win-col' : ''}`}>—</td>;
    }
    const isLow = lowestPerLine[l.id] === v.id;
    const val = unitMode === 'amt'
      ? `₹${money0(Number(q.baseRate) * Number(l.qty || 0))}`
      : `₹${money(q.baseRate)}`;
    return (
      <td key={v.id} className={`rate${isLow ? ' low' : ''}${isWin ? ' win-col' : ''}`}>
        <span className="r num">{val}</span>
        {isLow && <span className="chk">✓</span>}
        {unitMode === 'rate' && <span className="per">/{l.uom || 'unit'}</span>}
      </td>
    );
  };

  const vendorActions = (v) => (
    <div className="vendor-acts">
      <Button size="small" type="text" icon={<PrinterOutlined />} onClick={() => viewDoc(v)}>View</Button>
      {canQuoteRfq(rfq) && <Button size="small" icon={<DollarOutlined />} onClick={() => openQuote(v)}>
        {v.status === 'RESPONDED' ? 'Edit' : 'Record quote'}</Button>}
      {canAwardRfq(rfq) && v.status === 'RESPONDED' &&
        <Button size="small" type={lowest && v.id === lowest.id ? 'primary' : 'default'}
          icon={<TrophyOutlined />} onClick={() => doAward(v)}>Award</Button>}
      {editable && <Button size="small" type="text" danger
        onClick={() => run('rmv', () => rfqApi.removeVendor(id, v.id), 'Vendor removed.').then(load)}>Remove</Button>}
    </div>
  );

  return (
    <div className="rfq-v2" style={{ padding: '12px 14px' }}>
      <div className="rfq-detail-head">
        <Button size="small" icon={<ArrowLeftOutlined />} onClick={() => navigate('/erp/rfq')}>Back</Button>
        <h2>RFQ {rfq.rfqNo}</h2>
        <Tag color={rfqStatusColor(rfq.status)}>{rfqStatusLabel(rfq.status)}</Tag>
        <div style={{ flex: 1 }} />
        <Space wrap>
          <Tooltip title="Reload"><Button size="small" icon={<ReloadOutlined />} onClick={load} /></Tooltip>
          <Button size="small" icon={<PrinterOutlined />} onClick={() => viewDoc(null)}>Preview</Button>
          {editable && <Button size="small" icon={<PlusOutlined />} onClick={() => { setAddOpen(true); searchSuppliers(''); }}>Add vendors</Button>}
          {canSendRfq(rfq) && <Button size="small" type="primary" icon={<SendOutlined />} loading={busy === 'send'} onClick={doSend}>
            {rfq.status === 'DRAFT' ? 'Send to vendors' : 'Send reminder'}</Button>}
          {canCancelRfq(rfq) && <Button size="small" danger icon={<StopOutlined />} onClick={doCancel}>Cancel</Button>}
        </Space>
      </div>

      {/* Lifecycle rail */}
      <Card bordered={false} className="shadow-card rfq-rail" styles={{ body: { padding: '16px 20px' } }}>
        <Steps size="small" current={step.current} status={step.status} items={stepItems} />
      </Card>

      {rfq.status === 'CONVERTED' && rfq.convertedPoId && (
        <Alert type="success" showIcon style={{ marginBottom: 10 }}
          message="This RFQ was awarded and converted to a Purchase Order"
          description={<a onClick={() => navigate(`/erp/purchase/orders/${rfq.convertedPoId}`)}>Open the Purchase Order →</a>} />
      )}
      {rfq.status === 'CANCELLED' && (
        <Alert type="error" showIcon style={{ marginBottom: 10 }}
          message="This RFQ was cancelled."
          description={rfq.cancellationReason || null} />
      )}

      {/* Key-fact strip */}
      <Card bordered={false} className="shadow-card" style={{ marginBottom: 10 }} styles={{ body: { padding: 0 } }}>
        <div className="rfq-facts">
          <div className="fact">
            <div className="k">Submission due</div>
            <div className="v">{rfq.submissionDueDate ? dayjs(rfq.submissionDueDate).format('DD MMM YYYY') : '—'}
              {dueIn != null && rfq.status !== 'CONVERTED' && rfq.status !== 'CANCELLED' && (
                <Tag style={{ marginInlineStart: 8 }} color={dueIn < 0 ? 'red' : dueIn <= 2 ? 'gold' : 'default'}>
                  {dueIn < 0 ? `${Math.abs(dueIn)}d overdue` : dueIn === 0 ? 'due today' : `${dueIn}d left`}</Tag>)}
            </div>
          </div>
          <div className="fact">
            <div className="k">Vendors quoted</div>
            <div className="v num">{respondedCount} <small>/ {vendors.length}</small></div>
            <div className="meter"><i style={{ width: `${pct}%`, background: pct === 100 ? '#16a34a' : '#1877f2' }} /></div>
          </div>
          <div className="fact">
            <div className="k">Lowest total so far</div>
            <div className="v num">{lowest ? `₹${money0(lowest.total)}` : '—'}
              {winnerVendor && <small> · {winnerVendor.supplierName}</small>}</div>
          </div>
          <div className="fact">
            <div className="k">Source request</div>
            <div className="v" style={{ fontSize: 15 }}>{rfq.prNoSnapshot || '—'}
              {rfq.contactPerson && <small> · {rfq.contactPerson}</small>}</div>
          </div>
        </div>
      </Card>

      {/* Recommendation banner — only once a winner can be picked */}
      {showCompare && winnerVendor && (
        <div className="rfq-rec">
          <div className="medal"><TrophyOutlined /></div>
          <div className="copy">
            <div className="t">Recommend awarding <b>{winnerVendor.supplierName}</b> — lowest overall at ₹{money0(lowest.total)}</div>
            <div className="s">
              {lowest.hasRivals && lowest.savings > 0
                ? <><span className="save">Saves ₹{money0(lowest.savings)}</span> vs the next bid</>
                : 'Only bid received so far'}
              {leadFor(winnerVendor) !== '—' && ` · ${leadFor(winnerVendor)} lead`}
              {winnerVendor.quoteValidUntil && ` · valid till ${dayjs(winnerVendor.quoteValidUntil).format('DD MMM')}`}
              {maxDelta > 0 && lowest.total > 0 &&
                ` · bids ₹${money0(maxDelta)} (${((maxDelta / lowest.total) * 100).toFixed(1)}%) apart`}
            </div>
          </div>
          <Button type="primary" className="rfq-award-btn" icon={<TrophyOutlined />}
            loading={busy === 'award'} onClick={() => doAward(winnerVendor)}>Award &amp; create PO</Button>
        </div>
      )}

      {/* Comparison matrix — the hero */}
      <Card bordered={false} className="shadow-card" styles={{ body: { padding: 0 } }}
        title="Bid comparison"
        extra={vendors.length > 0 ? (
          <Segmented size="small" value={unitMode} onChange={setUnitMode}
            options={[{ label: 'Rate / unit', value: 'rate' }, { label: 'Extended amount', value: 'amt' }]} />
        ) : null}>
        {vendors.length === 0 ? (
          <div className="rfq-empty">No vendors yet — add vendors, then send the RFQ.</div>
        ) : (
          <>
            {showCompare && splitDiffers && splitGoods > 0 && (
              <div className="rfq-insight">
                💡 <span>Cheapest line-by-line totals <span className="num">₹{money0(splitGoods)}</span> in
                goods, but a different vendor wins some lines. Glass-i awards one vendor per PO, so the
                <b> quote total</b> decides the winner.</span>
              </div>
            )}
            <div className="rfq-matrix-scroll">
              <table className="rfq-matrix">
                <thead>
                  <tr>
                    <th className="c-item">Item</th>
                    <th className="c-qty">Qty</th>
                    {vendors.map((v) => (
                      <th key={v.id} className={lowest && v.id === lowest.id ? 'win-col' : ''}>
                        <div className="vh">
                          <span className="vname">{v.supplierName}</span>
                          <span className="vmeta">{v.supplierCode || ''} · <Tag style={{ marginInlineEnd: 0 }}
                            color={vendorStatusColor(v.status)}>{vendorStatusLabel(v.status)}</Tag></span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l) => (
                    <tr key={l.id}>
                      <td className="c-item">
                        <div className="item-main">{l.itemName}</div>
                        {l.itemDescription || l.itemCode
                          ? <div className="item-sub">{[l.itemCode, l.itemDescription].filter(Boolean).join(' · ')}</div>
                          : null}
                      </td>
                      <td className="c-qty num">{l.qty} <small>{l.uom}</small></td>
                      {vendors.map((v) => rateCell(v, l))}
                    </tr>
                  ))}

                  <tr className="sub sep">
                    <td className="c-item">Goods subtotal</td><td />
                    {vendors.map((v) => (
                      <td key={v.id} className={`rate${lowest && v.id === lowest.id ? ' win-col' : ''}`}>
                        {v.status === 'RESPONDED' ? <span className="num">₹{money0(goodsSubtotal(v))}</span>
                          : <span className="await-cell">—</span>}</td>))}
                  </tr>
                  <tr className="sub">
                    <td className="c-item">Freight + incidental</td><td />
                    {vendors.map((v) => (
                      <td key={v.id} className={`rate${lowest && v.id === lowest.id ? ' win-col' : ''}`}>
                        {v.status === 'RESPONDED'
                          ? <span className="num">₹{money0(Number(v.freightCharges) + Number(v.incidentalCharges))}</span>
                          : <span className="await-cell">—</span>}</td>))}
                  </tr>
                  <tr className="total">
                    <td className="c-item">Quote total</td><td />
                    {vendors.map((v) => {
                      const isWin = lowest && v.id === lowest.id;
                      if (v.status !== 'RESPONDED') {
                        return <td key={v.id} className={`rate await-cell${isWin ? ' win-col' : ''}`}
                          style={{ fontSize: 13, fontWeight: 500 }}>awaiting quote</td>;
                      }
                      const delta = Number(v.quoteTotal) - (lowest ? lowest.total : 0);
                      return (
                        <td key={v.id} className={`rate${isWin ? ' low win-col' : ''}`}>
                          <span className="r num">₹{money0(v.quoteTotal)}</span>
                          {isWin && <span className="chk">✓</span>}
                          {isWin
                            ? <div className="low-pill">Lowest bid</div>
                            : (delta > 0 && (
                              <>
                                <div className="delta">+₹{money0(delta)} <span className="pct">
                                  (+{lowest && lowest.total ? ((delta / lowest.total) * 100).toFixed(1) : '0'}%)</span></div>
                                <div className="tbar"><i style={{ width: `${maxDelta ? Math.round((delta / maxDelta) * 100) : 0}%` }} /></div>
                              </>))}
                        </td>);
                    })}
                  </tr>
                  <tr className="lead">
                    <td className="c-item">Lead time</td><td />
                    {vendors.map((v) => (
                      <td key={v.id} className={lowest && v.id === lowest.id ? 'win-col' : ''}>
                        {v.status === 'RESPONDED' ? leadFor(v) : <span className="await-cell">—</span>}</td>))}
                  </tr>
                  <tr className="act">
                    <td className="c-item" /><td />
                    {vendors.map((v) => (
                      <td key={v.id} className={lowest && v.id === lowest.id ? 'win-col' : ''}>{vendorActions(v)}</td>))}
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>

      {/* Lower region: items spec + activity / terms */}
      <div className="rfq-cols">
        <Card bordered={false} className="shadow-card" title={`Items requested (${lines.length})`}
          extra={rfq.prNoSnapshot ? <span className="rfq-muted">Snapshot from {rfq.prNoSnapshot}</span> : null}
          styles={{ body: { padding: 0 } }}>
          <Table size="small" rowKey="id" dataSource={lines} pagination={false}
            columns={[
              { title: '#', dataIndex: 'lineNo', width: 44, align: 'right' },
              { title: 'Item code', dataIndex: 'itemCode', width: 130, render: (v) => v || '—' },
              { title: 'Item', dataIndex: 'itemName',
                render: (v, r) => (<div><div style={{ fontWeight: 600 }}>{v}</div>
                  {r.itemDescription ? <div className="rfq-muted">{r.itemDescription}</div> : null}</div>) },
              { title: 'Qty', dataIndex: 'qty', width: 90, align: 'right' },
              { title: 'UOM', dataIndex: 'uom', width: 70 }
            ]} />
        </Card>

        <Card bordered={false} className="shadow-card" title="Activity" styles={{ body: { padding: '16px 18px 4px' } }}>
          <Timeline items={tl} />
          <Descriptions size="small" column={1} className="rfq-terms" colon={false}>
            <Descriptions.Item label="Payment">{rfq.paymentTerms || '—'}</Descriptions.Item>
            <Descriptions.Item label="Delivery">{rfq.deliveryTerms || '—'}</Descriptions.Item>
            <Descriptions.Item label="Freight">{rfq.freightTerms || '—'}</Descriptions.Item>
          </Descriptions>
        </Card>
      </div>

      {/* Add vendors modal */}
      <Modal open={addOpen} title="Add vendors from the Supplier Master" okText="Add"
        onOk={submitAddVendors} confirmLoading={busy === 'add'} onCancel={() => setAddOpen(false)} destroyOnClose>
        <p className="rfq-muted" style={{ marginTop: 0 }}>Pick one or more active suppliers. The RFQ is emailed to each vendor's address on file.</p>
        <Select mode="multiple" style={{ width: '100%' }} placeholder="Search suppliers…"
          filterOption={false} onSearch={searchSuppliers} onFocus={() => searchSuppliers('')}
          options={supplierOpts} value={pickedSuppliers} onChange={setPickedSuppliers} />
      </Modal>

      {/* Quote drawer */}
      <Drawer open={!!quoteVendor} width={820} title={quoteVendor ? `Quote — ${quoteVendor.supplierName}` : ''}
        onClose={() => setQuoteVendor(null)} destroyOnClose
        extra={<Button type="primary" loading={busy === 'quote'} onClick={submitQuote}>Save quote</Button>}>
        {quoteVendor && (
          <Form form={quoteForm} layout="vertical">
            <QuoteEditor form={quoteForm} lines={rfq.lines || []} />
          </Form>
        )}
      </Drawer>

      {/* Document viewer */}
      <Modal open={!!viewHtml} width={920} title="RFQ document" onCancel={() => setViewHtml(null)}
        footer={[
          <Button key="print" type="primary" icon={<PrinterOutlined />} onClick={printDoc}>Print / Save PDF</Button>,
          <Button key="close" onClick={() => setViewHtml(null)}>Close</Button>
        ]}>
        <iframe ref={iframeRef} title="rfq" srcDoc={viewHtml || ''} sandbox="allow-same-origin allow-modals"
          style={{ width: '100%', height: '70vh', border: '1px solid #e2e8f0', borderRadius: 6 }} />
      </Modal>
    </div>
  );
}
